import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, CreditCard, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import LogoutButton from "@/components/LogoutButton";
import { makeApi } from "@/lib/api";

const COPY = {
  payment_failed: ["Your payment didn't go through.", "We couldn't charge your card at renewal, so your access is paused. Update your payment method and your access comes back as soon as the payment succeeds."],
  canceled: ["Your subscription has ended.", "Your plan was cancelled, so your access is paused. Pick a plan to renew and pick up right where you left off — your data is saved."],
  ended: ["Your plan has expired.", "Your plan's service period is over, so your access is paused. Renew to keep using CarrierLaunch — your data is saved."],
};

export default function SubscriptionExpired() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/billing/status").then(({ data }) => {
      if (data.state !== "expired") navigate("/dashboard", { replace: true });
      else setS(data);
    }).catch(() => setS({ state: "expired", reason: "ended" }));
  }, [api, navigate]);

  const portal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal");
      window.location.href = data.url;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not open billing. Try again.");
      setBusy(false);
    }
  };
  const [title, body] = COPY[s?.reason] || COPY.ended;

  return (
    <div className="min-h-screen px-4 py-8 sm:py-14" data-testid="subscription-expired-page">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-10"><Logo /><LogoutButton testid="expired-logout-button" compact /></div>
        {!s ? <Loader2 className="animate-spin text-muted-foreground" /> : (
          <section className="brutal-border border-[#F87171]/60 bg-[#141414] p-6 sm:p-8">
            <AlertTriangle size={30} className="text-[#F87171] mb-4" />
            <h1 className="font-heading font-black text-3xl sm:text-4xl leading-tight" data-testid="expired-title">{title}</h1>
            <p className="text-muted-foreground mt-3" data-testid="expired-reason">{body}</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              {s.can_update_payment && (
                <button onClick={portal} disabled={busy} data-testid="expired-update-payment"
                  className="min-h-[48px] px-5 cta-primary font-mono-label text-xs inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} Update payment method
                </button>
              )}
              <Link to="/pricing" data-testid="expired-renew"
                className={`min-h-[48px] px-5 font-mono-label text-xs inline-flex items-center justify-center gap-2 ${s.can_update_payment ? "border border-[#262626] hover:border-white" : "cta-primary"}`}>
                <RefreshCw size={14} /> Renew — pick a plan
              </Link>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
